// R17 Wave 0 extraction — pure refactor, no behavior change
import React from 'react';
import { Printer, FileText, Download } from 'lucide-react';
import { SearchableSelect } from '../Common/SearchableSelect';

export type UserReportMode = 'all_with_house' | 'all_without_house' | 'specific_user';

export interface UserOption {
  id: number;
  label: string;
}

interface UserReportFiltersProps {
  startDate: string;
  endDate: string;
  mode: UserReportMode;
  userId: number | undefined;
  userOptions: UserOption[];
  loading: boolean;
  downloadingExcel: boolean;
  showExportButtons: boolean;
  error: string | null;
  onStartDateChange: (next: string) => void;
  onEndDateChange: (next: string) => void;
  onModeChange: (next: UserReportMode) => void;
  onUserIdChange: (next: number | undefined) => void;
  onGenerate: () => void;
  onPrintPdf: () => void;
  onDownloadExcel: () => void;
}

/**
 * Filters + action bar for the User Report page. Extracted from UserReport.tsx
 * in R17 Wave 0 so the parent stays under the 400-LOC cap once Task A adds
 * firm-totals + reconciliation columns.
 */
const UserReportFilters: React.FC<UserReportFiltersProps> = ({
  startDate,
  endDate,
  mode,
  userId,
  userOptions,
  loading,
  downloadingExcel,
  showExportButtons,
  error,
  onStartDateChange,
  onEndDateChange,
  onModeChange,
  onUserIdChange,
  onGenerate,
  onPrintPdf,
  onDownloadExcel,
}) => {
  const specificUserWithoutSelection = mode === 'specific_user' && !userId;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
      {/* Mode selector — controls who/what is included in the report */}
      <fieldset className="mb-4">
        <legend className="block text-sm font-medium text-gray-700 mb-2">
          Report Mode
        </legend>
        <div className="flex flex-col sm:flex-row gap-3" role="radiogroup" aria-label="Report mode">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="report-mode"
              value="all_with_house"
              checked={mode === 'all_with_house'}
              onChange={() => onModeChange('all_with_house')}
              className="h-4 w-4 text-primary-600 border-gray-300 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700">All Users + House</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="report-mode"
              value="all_without_house"
              checked={mode === 'all_without_house'}
              onChange={() => onModeChange('all_without_house')}
              className="h-4 w-4 text-primary-600 border-gray-300 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700">All Users (no House)</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="report-mode"
              value="specific_user"
              checked={mode === 'specific_user'}
              onChange={() => onModeChange('specific_user')}
              className="h-4 w-4 text-primary-600 border-gray-300 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700">Specific User</span>
          </label>
        </div>
      </fieldset>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <label htmlFor="start-date" className="block text-sm font-medium text-gray-700 mb-2">
            Start Date
          </label>
          <input
            type="date"
            id="start-date"
            value={startDate}
            onChange={(e) => onStartDateChange(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        <div>
          <label htmlFor="end-date" className="block text-sm font-medium text-gray-700 mb-2">
            End Date
          </label>
          <input
            type="date"
            id="end-date"
            value={endDate}
            onChange={(e) => onEndDateChange(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        {mode === 'specific_user' ? (
          <div>
            <label htmlFor="user" className="block text-sm font-medium text-gray-700 mb-2">
              Employee
            </label>
            <SearchableSelect
              options={userOptions}
              value={userId}
              onChange={onUserIdChange}
              placeholder="employees"
              allLabel="All Employees"
            />
          </div>
        ) : (
          <div aria-hidden="true" />
        )}

        {/* Guard: in specific_user mode, an unset user means the request
            would silently degrade to all-users — actions disabled in that
            state to avoid producing a confusing "specific user" report
            that's actually firm-wide. */}
        <div className="flex items-end gap-3">
          <button
            onClick={onGenerate}
            disabled={loading || !startDate || !endDate || specificUserWithoutSelection}
            className="flex-1 bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
          >
            <FileText className="h-4 w-4 mr-2" />
            Generate
          </button>

          {showExportButtons && (
            <>
              <button
                onClick={onPrintPdf}
                disabled={loading || specificUserWithoutSelection}
                className="flex-1 bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
              >
                <Printer className="h-4 w-4 mr-2" />
                Print
              </button>
              <button
                onClick={onDownloadExcel}
                disabled={downloadingExcel || specificUserWithoutSelection}
                className="flex-1 bg-emerald-700 text-white px-6 py-2 rounded-lg hover:bg-emerald-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
              >
                <Download className="h-4 w-4 mr-2" />
                {downloadingExcel ? 'Downloading...' : 'Excel'}
              </button>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}
    </div>
  );
};

export default UserReportFilters;
